import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "react-bootstrap";
import { revertToSuperAdmin } from "../../redux/actions/auth";
import { modeUsers } from "../../redux/services/user.service";
import SideBarMenu from "./sidebar";
import { getSearchList } from "../../redux/services/caseDetail.service";
import AlarmNotification from "./alarmNotification";
import { useDebouncedCallback } from "../../constants/useDebouncedCallback";
import useRoles from "../../constants/useRoles";
import UnsavedChangesModal from "./UnsavedChangesModal";
import { ADD } from "../../redux/actions/applicationType";
import { performLogout } from "../../constants/logoutHelper";
import { useNotificationCount } from "../../constants/useNotificationCount";
const THEME_KEY = "themeMode";

const AuthorityLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { readOnly, admin, readWrite } = useRoles();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const formValueState = useSelector((state) => state.formState);

  const [sidebarActive, setSidebarActive] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");

  const [theme, setTheme] = useState(() => {
    return localStorage.getItem(THEME_KEY) ?? user?.themeMode ?? "light";
  });

  const [searchCaseRequest, setSearchCaseRequest] = useState({
    AuthorityId: user?.auth_id || null,
    WebUserId: admin ? null : user?.webUserId || null,
    SearchTerm: "",
    Status: -1,
    SortCol: "CaseManagerDetailId",
    SortDir: "asc",
    PageNo: 1,
    PageSize: 10
  });
  const searchCaseRequestRef = useRef(searchCaseRequest);
  // UnsavedChangesModal states
  const [showModal, setShowModal] = useState(false);
  const [pendingRoute, setPendingRoute] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);

  const handleSidebar = () => setSidebarActive(!sidebarActive);
  const handleRevertClick = () => {
    dispatch(revertToSuperAdmin())
      .then(() => navigate("/dashboard"))
      .catch((err) => console.error("Error during revert:", err));
  };
  const showBackdrop = window.outerWidth <= 1200 && sidebarActive;

  useEffect(() => {
    if (window.outerWidth <= 1200) {
      queueMicrotask(() => {
        setSidebarActive(false);
      });
    }
  }, [location.pathname]);
  const adminName = user?.first_name ?? "";

  const notificationCount = useNotificationCount(location.pathname);

  const count = readOnly ? 0 : notificationCount;
  useEffect(() => {
    function handleResize() {
      setSidebarActive(window.outerWidth > 1200);
    }

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);
  useEffect(() => {
    document.body.className = theme;
    document.documentElement.setAttribute("data-bs-theme", theme);
  }, [theme]);

  useEffect(() => {
    const savedTheme = localStorage.getItem(THEME_KEY);
    if (!savedTheme && user?.themeMode) {
      //setTheme(user.themeMode);
      localStorage.setItem(THEME_KEY, user.themeMode);
      const localStorageUser = JSON.parse(localStorage.getItem("user") || "{}");
      localStorageUser.themeMode = user.themeMode;
      localStorage.setItem("user", JSON.stringify(localStorageUser));
    }
  }, [user]);

  const toggleDarkTheme = () => {
    const newTheme = theme === "dark" ? "light" : "dark";
    setTheme(newTheme);
    localStorage.setItem(THEME_KEY, newTheme);
    const localStorageUser = JSON.parse(localStorage.getItem("user") || "{}");
    localStorageUser.themeMode = newTheme;
    localStorage.setItem("user", JSON.stringify(localStorageUser));
    sendThemeToServer(newTheme);
  };

  const sendThemeToServer = (newTheme) => {
    modeUsers(newTheme)
      .then((response) => {
        if (response.states === "error") throw new Error("Failed to send theme to server");
      })
      .catch((error) => console.error("Error sending theme to server:", error));
  };

  useEffect(() => {
    searchCaseRequestRef.current = searchCaseRequest;
  }, [searchCaseRequest]);

  const debouncedSearch = useDebouncedCallback(async (value) => {
    if (!value.trim()) {
      setSearchResults([]);
      setErrorMessage("");
      return;
    }

    const updated = {
      ...searchCaseRequestRef.current,
      SearchTerm: value
    };

    setSearchCaseRequest(updated);
    const data = await getSearchList(updated);
    const results = data?.data?.data || [];
    setSearchResults(results);
    setErrorMessage(results.length === 0 ? "Oops! No Case Matched 🥲" : "");
  }, 500);

  const handleChanges = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedSearch(value);
  };

  const handleLogout = () => {
    performLogout(dispatch);
  };

  const handleProtectedNavigation = (e, route, action = null) => {
    if (formValueState?.isDirty) {
      e?.preventDefault();
      setPendingRoute(route);
      setPendingAction(action);
      setShowModal(true);
      return;
    }
    if (action) action();
    else if (route) navigate(route);
  };

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (formValueState?.isDirty) {
        e.preventDefault();
        e.returnValue = "You have unsaved changes. Are you sure you want to leave without saving?";
        return "You have unsaved changes. Are you sure you want to leave without saving?";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [formValueState?.isDirty]);
  return (
    <>
      <div id="app">
        <div id="sidebar" className={sidebarActive ? "active" : "inactive"}>
          <div className="sidebar-wrapper active ">
            <div className="sidebar-header position-relative">
              <div className="d-flex justify-content-between align-items-center">
                <div className="logo">
                  <button
                    type="button"
                    onClick={(e) => handleProtectedNavigation(e, "/authority/case/list")}
                    className="text-decoration-none border-0 bg-transparent p-0">
                    Case Manager
                  </button>
                </div>
                <div className="sidebar-toggler">
                  <Link className="sidebar-hide d-xl-none d-block" onClick={handleSidebar}>
                    <i className="pi pi-times-circle"></i>
                  </Link>
                </div>
              </div>
            </div>
            <SideBarMenu notificationCount={notificationCount} />
          </div>
        </div>
        <div id="main" className="layout-navbar navbar-fixed">
          <header>
            <nav className="navbar navbar-expand navbar-light navbar-top border-bottom-custom p-2 h-unset">
              <div className="container-fluid flex-wrap gap-2">
                <div className="d-inline-block position-relative col-md-6 col-12">
                  <Button className="p-2 d-inline-block" onClick={handleSidebar}>
                    <i className="pi pi-align-justify fs-4 d-flex align-items-center justify-content-center h-100"></i>
                  </Button>
                  <div
                    className="d-inline-block"
                    style={{ marginLeft: "8px", width: "calc(100% - 60px)" }}>
                    <div className="input-group justify-content-center">
                      <input
                        type="text"
                        className="form-control fs-6 py-2"
                        placeholder="Search Case Number/Client Name/PostCode"
                        aria-label="Search"
                        value={searchTerm}
                        onChange={handleChanges}
                      />
                      <span className="input-group-text d-flex align-items-center px-3">
                        <i className="pi pi-search"></i>
                      </span>
                    </div>
                  </div>

                  {searchTerm && (
                    <ul
                      className="list-group position-absolute z-3 w-100 border rounded mt-1 shadow"
                      style={{ maxHeight: "200px", overflowY: "auto", backgroundColor: "#fff" }}>
                      {searchResults.length > 0 ? (
                        searchResults.map((result, index) => (
                          <li key={index} className="list-group-item p-2">
                            <a
                              href="#"
                              className="text-decoration-none text-dark"
                              onClick={(e) =>
                                handleProtectedNavigation(
                                  e,
                                  `/case-manager/edit/${result.caseManagerDetailId}`
                                )
                              }>
                              <div>
                                <span className="badge bg-primary me-2">
                                  {result.caseManagerDetailId}
                                </span>
                                <span className="fw-bold">
                                  {result.firstName} {result.lastName}
                                </span>
                              </div>
                              <div style={{ fontSize: 13 }}>
                                <i className="pi pi-map-marker me-1 text-danger"></i>
                                {result.address}, {result.postCode}
                              </div>
                            </a>
                          </li>
                        ))
                      ) : errorMessage ? (
                        <li className="list-group-item text-center text-danger py-2">
                          <i className="pi pi-info-circle me-2"></i>
                          {errorMessage}
                        </li>
                      ) : null}
                    </ul>
                  )}
                </div>

                <div
                  className="collapse navbar-collapse justify-content-lg-end justify-content-sm-between justify-content-between gap-3"
                  id="navbarSupportedContent">
                  {readWrite && (
                    <AlarmNotification
                      isDirty={formValueState?.isDirty}
                      setShowModal={setShowModal}
                      setPendingAction={setPendingAction}
                    />
                  )}
                  {user?.impersonating && (
                    <button onClick={handleRevertClick} className="btn btn-warning custom-btn m-0">
                      Back to Admin Dashboard
                    </button>
                  )}

                  <div className="mode m-0">
                    <div className="theme-toggle d-flex gap-2 align-items-center mt-0">
                      <div className="form-check form-switch fs-6">
                        <input
                          className="form-check-input me-0"
                          type="checkbox"
                          id="toggle-dark"
                          style={{ cursor: "pointer" }}
                          checked={theme === "dark"}
                          onChange={toggleDarkTheme}
                        />
                        <label
                          htmlFor="darkModeSwitch"
                          className="form-check-label"
                          style={{ cursor: "pointer", fontSize: "20px" }}>
                          {theme === "dark" ? (
                            <i className="bi bi-moon"></i>
                          ) : (
                            <i className="bi bi-brightness-high"></i>
                          )}
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* User menu */}
                  <div className="dropdown">
                    <a href="/admin" data-bs-toggle="dropdown" aria-expanded="false">
                      <div className="user-menu d-flex">
                        <div className="user-name text-end me-3">
                          <h6 className="mb-0 text-gray-600">{adminName}</h6>
                          <p className="mb-0 text-sm text-gray-600">
                            {readOnly
                              ? "Read Only User"
                              : admin
                                ? user.isPrimaryContact
                                  ? "Authority"
                                  : "Super User"
                                : "Officer"}
                          </p>
                        </div>
                        <div className="user-img align-items-center text-gray-600">
                          <i className="pi pi-user fs-4"></i>
                        </div>
                      </div>
                    </a>
                    <ul className="dropdown-menu dropdown-menu-end" style={{ minWidth: "11rem" }}>
                      <li>
                        <h6 className="dropdown-header">Hello, {adminName}!</h6>
                      </li>
                      <li>
                        <a
                          href="#"
                          className="dropdown-item"
                          onClick={(e) => handleProtectedNavigation(e, "/profile")}>
                          <i className="icon-mid pi pi-user me-2"></i> My Profile
                        </a>
                      </li>
                      <li>
                        <a
                          href="#"
                          className="dropdown-item"
                          onClick={(e) => handleProtectedNavigation(e, "/change-password")}>
                          <i className="icon-mid pi pi-key me-2"></i> Change Password
                        </a>
                      </li>
                      <li>
                        <hr className="dropdown-divider" />
                      </li>
                      <li>
                        <a
                          href="#"
                          className="dropdown-item"
                          onClick={(e) => {
                            if (formValueState?.isDirty) {
                              e.preventDefault();
                              setPendingAction(() => handleLogout); // <-- store action
                              setShowModal(true);
                            } else {
                              handleLogout(); // no dirty changes
                            }
                          }}>
                          <i className="icon-mid pi pi-arrow-left me-2"></i> Logout
                        </a>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </nav>
          </header>

          <div id="main-content" className="pt-1">
            <Outlet />
          </div>
          <footer></footer>
        </div>
      </div>

      <div
        className="sidebar-backdrop"
        style={{ display: showBackdrop ? "block" : "none" }}
        onClick={handleSidebar}></div>

      {/* UnsavedChangesModal */}
      <UnsavedChangesModal
        show={showModal}
        onLeave={() => {
          dispatch({
            type: ADD,
            payload: false,
            propertyKey: "isDirty"
          });
          setShowModal(false);
          if (pendingAction) pendingAction();
          else if (pendingRoute) navigate(pendingRoute);
          setPendingRoute(null);
          setPendingAction(null);
        }}
        onStay={() => {
          setShowModal(false);
          setPendingRoute(null);
          setPendingAction(null);
        }}
      />
    </>
  );
};

export default AuthorityLayout;
